import { useQuery } from "@apollo/client";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useCurrentUser } from "hooks/useCurrentUser";
import { buildFeatureFlagMap } from "hooks/useFeatureFlags";
import { GET_TENANT_QUERY, useTenantId } from "hooks/useTenant";
import { PropsWithChildren } from "react";
import { Navigate } from "react-router-dom";

/**
 * The flag gate for the inbox, which `HasFlag` cannot express.
 *
 * Notifications are addressed to a person, not an organization, so the inbox
 * lives at `/notifications` rather than under `/o/:tenantId`. Tenant context
 * there comes only from session storage, which is per-tab and routinely empty:
 * a staff user who has not opened an organization this session, or anyone who
 * follows a link into a fresh tab. `HasFlag` reads that absence as every flag
 * being off and redirects, which bounced readers out of their own notifications
 * and, for staff, all the way back to `/staff`.
 *
 * So the gate resolves the deciding organization itself rather than inferring
 * one from the tab. Staff pass because this flag carries a staff override they
 * hold whatever organization they are in; everyone else is judged by their own
 * organization's flag, which the user record carries whether or not this tab
 * has ever been to an organization page — so an empty tab is not a way around
 * a flag the organization has turned off.
 */
export function NotificationsFlagGate({ children }: PropsWithChildren) {
  const sessionTenantId = useTenantId();
  const { user, isStaff, loading: userLoading } = useCurrentUser();

  const tenantId = sessionTenantId ?? user?.UserTenant[0]?.tenantId;

  const { data, loading: tenantLoading } = useQuery(GET_TENANT_QUERY, {
    variables: { tenantId: tenantId ?? "" },
    skip: isStaff || !tenantId,
  });

  if (userLoading) {
    return null;
  }

  if (isStaff) {
    return <>{children}</>;
  }

  if (tenantLoading) {
    return null;
  }

  const featureFlags = buildFeatureFlagMap(data?.tenant?.featureFlags, isStaff);

  if (!featureFlags[FeatureFlagKey.Notifications]) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
